import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { configureStore } from "@reduxjs/toolkit";
import { errorSlice } from "@unleashed/slices/errorSlice";
import { AdminApi } from "../features/core/api/AdminApi";
import { AuthSlice } from "./AuthSlice";
import { MenuSlice } from "./MenuSlice";
import { AdminConfigSlice } from "./AdminConfigSlice";
import { PosSlice } from "./PosSlice";
import { AdminAccountApi } from "../features/core/api/AdminAccountApi";
import { AdminProductsApi as ProductsApi } from "../features/core/api/AdminProductsApi";
import { PosConfigSlice } from "./PosConfigSlice";
import { CameraSlice } from "./CameraSlice";
import { AdminSecurityApi } from "../features/core/api/AdminSecurityApi";
import { PosBasePrinterApi as PrinterApi } from "../features/core/api/PosBasePrinterApi";
import { AdminInvoiceApi } from "../features/core/api/AdminInvoiceApi";
import { WingmanAdminAPI } from "../features/chatbot/api/wingmanAdminAPI";
import modalFormReducer from "./ModalFormSlice";
import photoModalReducer from "@unleashed-admin/stores/PhotoModalSlice";
import { BookingSlice } from "./BookingSlice";
import { BulkProductSlice } from "./BulkProductSlice";

export const AdminStore = configureStore({
  reducer: {
    error: errorSlice.reducer,
    auth: AuthSlice.reducer,
    menu: MenuSlice.reducer,
    config: AdminConfigSlice.reducer,
    pos: PosSlice.reducer,
    posConfigSlice: PosConfigSlice.reducer,
    camera: CameraSlice.reducer,
    modalForm: modalFormReducer,
    photoModal: photoModalReducer,
    booking: BookingSlice.reducer,
    bulkProduct: BulkProductSlice.reducer,
    [AdminApi.reducerPath]: AdminApi.reducer,
    [AdminAccountApi.reducerPath]: AdminAccountApi.reducer,
    [ProductsApi.reducerPath]: ProductsApi.reducer,
    [AdminInvoiceApi.reducerPath]: AdminInvoiceApi.reducer,
    [AdminSecurityApi.reducerPath]: AdminSecurityApi.reducer,
    [PrinterApi.reducerPath]: PrinterApi.reducer,
    [WingmanAdminAPI.reducerPath]: WingmanAdminAPI.reducer,
  },
  // Note: if this is throwing a big error, you probably
  // used the same `reducerPath` for two APIs
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat([
      AdminApi.middleware,
      AdminAccountApi.middleware,
      ProductsApi.middleware,
      AdminInvoiceApi.middleware,
      AdminSecurityApi.middleware,
      PrinterApi.middleware,
      WingmanAdminAPI.middleware,
    ]),
});

export type AdminState = ReturnType<typeof AdminStore.getState>;
type AdminDispatch = typeof AdminStore.dispatch;

export const useAdminDispatch = () => useDispatch<AdminDispatch>();
export const useAdminSelector: TypedUseSelectorHook<AdminState> = useSelector;
